import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ButtonIcon } from '@resideo/blueprint-oneportal';
import { DeviceOperationType } from '@resideo/web-integration-services-api-client';
import type { CameraV2Operation } from '@resideo/web-integration-services-api-client';
import { permissionService } from '@/services/permissionService';
import { chevronLeftIcon } from '@/assets/icons';
import { manageDevicesService } from '@/api/services/manageDevicesServices';
import DeterrenceSection, { type DeterrenceSettings } from './sections/DeterrenceSection';
import ArmingModesSection from './sections/ArmingModesSection';
import RecordingSection from './sections/RecordingSection';
import NotificationSetting from './sections/NotificationSetting';
import type { BulkChange } from './ReviewApplyPage';
import type { RecordingBlock } from './recording/types';
import styles from './BulkEditSettings.module.css';

type SectionId = 'deterrence' | 'armingModes' | 'recording' | 'notifications';

const SECTION_IDS: SectionId[] = ['deterrence', 'armingModes', 'recording', 'notifications'];

/** Convert timeout string (e.g. "30s") to seconds number */
function parseTimeoutSeconds(timeout: string): number {
  return parseInt(timeout.replace('s', ''), 10) || 30;
}

// ─── Video Config Types & Defaults ───────────────────────────────────────────
interface VideoSettings {
  geometry: { Width: number; Height: number };
  rotate: boolean;
  quality: string;
  hevc: boolean;
  fps: number;
}

interface VideoConfig {
  mainStream: { video: VideoSettings };
  subStream: { video: VideoSettings };
  continuous: { enabled: boolean; video: VideoSettings; forceLowRes: boolean };
  events: { enabled: boolean; video: VideoSettings; maxRecordingDurationSeconds: number; postEventTimeoutSeconds: number };
}

const DEFAULT_VIDEO_4K: VideoSettings = { geometry: { Width: 3840, Height: 2160 }, rotate: false, quality: 'high', hevc: false, fps: 15 };
const DEFAULT_VIDEO_1080P: VideoSettings = { geometry: { Width: 1920, Height: 1080 }, rotate: false, quality: 'high', hevc: false, fps: 15 };

const DEFAULT_VIDEO_CONFIG: VideoConfig = {
  mainStream: { video: DEFAULT_VIDEO_4K },
  subStream: { video: DEFAULT_VIDEO_1080P },
  continuous: { enabled: true, video: DEFAULT_VIDEO_4K, forceLowRes: false },
  events: { enabled: true, video: DEFAULT_VIDEO_1080P, maxRecordingDurationSeconds: 30, postEventTimeoutSeconds: 30 },
};

/** Map API video shape (lowercase geometry keys) to payload shape (uppercase). */
function mapVideoSettings(apiVideo: any): VideoSettings {
  if (!apiVideo) return DEFAULT_VIDEO_1080P;
  return {
    geometry: { Width: apiVideo.geometry?.width ?? 1920, Height: apiVideo.geometry?.height ?? 1080 },
    rotate: apiVideo.rotate ?? false,
    quality: apiVideo.quality ?? 'high',
    hevc: apiVideo.hevc ?? false,
    fps: apiVideo.fps ?? 15,
  };
}

function blocksToSchedulePayload(blocks: RecordingBlock[], scheduleName: string) {
  return {
    recordingScheduleConfig: {
      schedules: [{
        name: scheduleName,
        enabled: true,
        priority: 1,
        timeWindows: blocks.map(b => ({
          daysOfWeek: b.daysOfWeek,
          startTime: b.startTime,
          endTime: b.endTime,
          clipEventRecording: b.clipEventRecording,
          snapshot: b.snapshot,
          continuousRecording: b.continuousRecording,
        })),
      }],
    },
  };
}

const BulkEditSettings: React.FC<{ api?: any }> = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const cameraIds = (searchParams.get('cameras') ?? '').split(',').filter(Boolean);
  const count = cameraIds.length;
  const notificationsTabEnabled = permissionService.isFeatureEnabled('cameras.notifications');
  const state = location.state as {
    nonTrumanCameraCount?: number;
    scheduleBlocks?: import('./recording/types').RecordingBlock[];
    activeSection?: SectionId;
  } | null;
  const nonTrumanCameraCount = state?.nonTrumanCameraCount ?? 0;

  // When returning from ReplaceSchedulePage, build the API payload from schedule blocks.
  React.useEffect(() => {
    if (state?.scheduleBlocks) {
      setOperation(DeviceOperationType.CAMERA_UPDATE_RECORDING_SCHEDULE,
        blocksToSchedulePayload(state.scheduleBlocks, t('bulkEditSettings.recording.scheduleName'))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.scheduleBlocks]);

  const [activeSection, setActiveSection] = useState<SectionId>(
    state?.activeSection === 'notifications' && notificationsTabEnabled ? 'notifications' : 'deterrence'
  );

  const visibleSections = notificationsTabEnabled
    ? SECTION_IDS
    : SECTION_IDS.filter(id => id !== 'notifications');

  const videoConfigRef = useRef<VideoConfig>({ ...DEFAULT_VIDEO_CONFIG });

  useEffect(() => {
    if (cameraIds.length === 0) return;
    manageDevicesService.fetchCameraState(cameraIds[0])
      .then(state => {
        const cfg = state?.cameraSettings?.advancedVideoConfig;
        if (cfg) {
          videoConfigRef.current = {
            mainStream: { video: mapVideoSettings(cfg.mainStream?.video) },
            subStream: { video: mapVideoSettings(cfg.subStream?.video) },
            continuous: {
              enabled: cfg.continuous?.enabled ?? true,
              video: mapVideoSettings(cfg.continuous?.video),
              forceLowRes: cfg.continuous?.forceLowRes ?? false,
            },
            events: {
              enabled: cfg.events?.enabled ?? true,
              video: mapVideoSettings(cfg.events?.video),
              maxRecordingDurationSeconds: cfg.events?.maxRecordingDurationSeconds ?? 30,
              postEventTimeoutSeconds: cfg.events?.postEventTimeoutSeconds ?? 30,
            },
          };
        }
      })
      .catch(err => console.warn('Failed to fetch camera state, using defaults:', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraIds[0]]);

  // Track changed settings for Review page — seeded with each section's
  // default value on mount, then overwritten as the user changes settings.
  const changesRef = React.useRef<Record<string, string>>({});
  const setChange = (label: string, value: string) => { changesRef.current[label] = value; };

  // Track structured API operations in parallel with display changes.
  const operationsRef = React.useRef<CameraV2Operation[]>([]);
  const setOperation = (type: DeviceOperationType, payload: Record<string, unknown>) => {
    const idx = operationsRef.current.findIndex(op => op.operationType === type);
    const op: CameraV2Operation = { operationType: type, payload: payload as any };
    if (idx >= 0) operationsRef.current[idx] = op;
    else operationsRef.current.push(op);
  };

  const pageTitle = count === 1
    ? t('bulkEditSettings.pageTitle', { count })
    : t('bulkEditSettings.pageTitlePlural', { count });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <ButtonIcon size="small" iconType="icon" aria-label={t('common.back')} onClick={() => navigate(-1)}>
          <img src={chevronLeftIcon} className={styles.iconSmall} alt="" aria-hidden="true" />
        </ButtonIcon>
        <h1 className={styles.pageTitle}>{pageTitle}</h1>
      </div>
      <div className={styles.mainBody}>
        <nav className={styles.sidebar} role="navigation">
          {visibleSections.map(id => (
            <Button
              key={id}
              variant="text"
              className={[styles.sideItem, activeSection === id && styles.sideItemActive].filter(Boolean).join(' ')}
              onClick={() => setActiveSection(id)}
            >
              {t(`bulkEditSettings.sections.${id}`)}
            </Button>
          ))}
        </nav>
        <div className={styles.contentArea}>
          {activeSection === 'deterrence' && (
            <DeterrenceSection
              onPassiveDeterrenceChange={(v, settings) => {
                setChange(t('bulkEditSettings.review.changeLabels.passiveDeterrence'), v);
                setOperation(DeviceOperationType.CAMERA_UPDATE_DETERRENCE_CONFIGURATION, {
                  activeDeterrence: [
                    {
                      name: settings.name,
                      lightPattern: settings.lightStyle,
                      sound: settings.soundEffect,
                      volume: settings.soundVolume,
                      offTime: parseTimeoutSeconds(settings.timeout),
                    },
                  ],
                  panicDeterrenceName: settings.name,
                  passiveDeterrence: { enable: v === t('common.enabled') },
                });
              }}
              onActiveDeterrenceSettingsChange={settings => {
                // Update existing deterrence operation if one exists, or create new one
                const existingOp = operationsRef.current.find(
                  op => op.operationType === DeviceOperationType.CAMERA_UPDATE_DETERRENCE_CONFIGURATION
                );
                setOperation(DeviceOperationType.CAMERA_UPDATE_DETERRENCE_CONFIGURATION, {
                  ...(existingOp?.payload ?? {}),
                  activeDeterrence: [
                    {
                      name: settings.name,
                      lightPattern: settings.lightStyle,
                      sound: settings.soundEffect,
                      volume: settings.soundVolume,
                      offTime: parseTimeoutSeconds(settings.timeout),
                    },
                  ],
                  panicDeterrenceName: settings.name,
                });
              }}
            />
          )}
          {activeSection === 'armingModes' && (
            <ArmingModesSection
              onBehaviorChange={(modeKey, uiValue) => {
                const ARM_MODE_API_VALUES: Record<string, { modeAction: string; deterrenceSetName: string | null }> = {
                  none: { modeAction: 'noDeterrence', deterrenceSetName: null },
                  privacyMode: { modeAction: 'privacy', deterrenceSetName: null },
                  default: { modeAction: 'enableDeterrence', deterrenceSetName: 'Default' },
                };
                // All 4 arm modes must always be present together in the payload.
                const ARM_MODE_DEFAULTS = {
                  disarmed: { modeAction: 'noDeterrence', deterrenceSetName: null },
                  armStay: { modeAction: 'privacy', deterrenceSetName: null },
                  armAway: { modeAction: 'privacy', deterrenceSetName: null },
                  armNight: { modeAction: 'enableDeterrence', deterrenceSetName: 'Default' },
                };
                setChange(
                  t(`bulkEditSettings.armingModes.modes.${modeKey}.label`),
                  t(`bulkEditSettings.armingModes.behaviorOptions.${uiValue}`)
                );
                const updatedPayload = {
                  ...ARM_MODE_DEFAULTS,
                  ...operationsRef.current.find(op => op.operationType === DeviceOperationType.CAMERA_UPDATE_ARM_MODE_CONFIGURATION)?.payload,
                  [modeKey]: ARM_MODE_API_VALUES[uiValue] ?? { modeAction: uiValue, deterrenceSetName: null },
                };
                setOperation(DeviceOperationType.CAMERA_UPDATE_ARM_MODE_CONFIGURATION, updatedPayload);
              }}
            />
          )}
          {activeSection === 'recording' && (
            <RecordingSection
              onEventRecordingChange={v => {
                setChange(t('bulkEditSettings.review.changeLabels.defaultEventRecording'), v);
                const videoConfig = videoConfigRef.current;
                const existingPayload = operationsRef.current.find(op => op.operationType === DeviceOperationType.CAMERA_UPDATE_ADVANCED_VIDEO_CONFIGURATION)?.payload;
                setOperation(DeviceOperationType.CAMERA_UPDATE_ADVANCED_VIDEO_CONFIGURATION, {
                  ...videoConfig,
                  ...existingPayload,
                  events: { ...videoConfig.events, ...(existingPayload?.events as object), enabled: v !== t('common.disabled') },
                });
              }}
              onContinuousRecordingChange={v => {
                setChange(t('bulkEditSettings.review.changeLabels.defaultContinuousRecording'), v);
                const videoConfig = videoConfigRef.current;
                const existingPayload = operationsRef.current.find(op => op.operationType === DeviceOperationType.CAMERA_UPDATE_ADVANCED_VIDEO_CONFIGURATION)?.payload;
                setOperation(DeviceOperationType.CAMERA_UPDATE_ADVANCED_VIDEO_CONFIGURATION, {
                  ...videoConfig,
                  ...existingPayload,
                  continuous: { ...videoConfig.continuous, ...(existingPayload?.continuous as object), enabled: v !== t('common.disabled') },
                });
              }}
            />
          )}
          {
            notificationsTabEnabled && activeSection === 'notifications' && (
              <NotificationSetting />
            )
          }
        </div>
      </div>
      <div className={styles.footer}>
        <Button variant="secondary" size="small" onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" size="small" onClick={() => {
          const changes: BulkChange[] = Object.entries(changesRef.current).map(([label, value]) => ({ label, value }));
          const cameras = searchParams.get('cameras') ?? '';
          // Merge persisted schedule blocks (from ReplaceSchedulePage) into the recording operation.
          const savedBlocks = state?.scheduleBlocks;
          if (savedBlocks) {
            setOperation(DeviceOperationType.CAMERA_UPDATE_RECORDING_SCHEDULE,
              blocksToSchedulePayload(savedBlocks, t('bulkEditSettings.recording.scheduleName'))
            );
          }
          navigate(`/camera/bulk-edit/review?cameras=${encodeURIComponent(cameras)}`, { state: { changes, count, nonTrumanCameraCount, cameraOperations: operationsRef.current } });
        }}>
          {t('bulkEditSettings.reviewButton')}
        </Button>
      </div>
    </div>
  );
};

export default BulkEditSettings;
